import type { Room } from '@hollowveil/shared';

/**
 * Terre du Milieu - "Eärwen, l'Elfe qui Apprenait Autrement"
 *
 * Personnage à aider: Eärwen (jeune elfe)
 * Problématique: Dans une société elfique "parfaite", elle apprend différemment
 *
 * En spectral (Mordor émotionnel): On voit ses murs de protection et ses créations cachées
 *
 * Ce que Côtelette apprend: L'accompagnement - guider sans forcer
 */
export const roomMiddleEarthReveil: Room = {
  id: 'room_middle_earth_reveil',
  name: 'Terre du Milieu - Eärwen, l\'Elfe qui Apprenait Autrement',
  description: 'Fondcombe, havre des elfes. Magnifique et serein... mais pour certains, oppressant.',
  size: [28, 14, 28],
  floorStyle: 'marble',
  palette: 'mystical',
  initialWorld: 'real',
  hasSpectralLayer: true,
  ambiance: {
    realWorld: {
      backgroundColor: '#90caf9',
      ambientLightColor: '#ffd700',
      ambientLightIntensity: 0.8,
      fogColor: '#e8f4f8',
      fogDensity: 0.015,
    },
    spectralWorld: {
      // Mordor émotionnel - comment Eärwen VOIT son monde
      backgroundColor: '#1a0a0a',
      ambientLightColor: '#ff4500',
      ambientLightIntensity: 0.25,
      fogColor: '#2d1414',
      fogDensity: 0.06,
    },
  },
  objects: [
    // === EÄRWEN ===
    {
      id: 'obj_earwen',
      name: 'Eärwen',
      type: 'interactive',
      modelId: 'npc_elf_young',
      position: [8, 0, 20],
      rotation: [0, Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        state: 'shy',
        dialogueId: 'earwen_intro',
      },
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddleId: 'earwen_first_contact',
          },
        },
      ],
    },

    // Murs de protection d'Eärwen (spectral)
    {
      id: 'obj_protection_wall_1',
      name: 'Mur de Protection',
      type: 'static',
      modelId: 'invisible_wall',
      position: [7, 0, 19],
      rotation: [0, 0.2, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        emotion: 'fear',
        color: '#8b0000',
      },
    },
    {
      id: 'obj_protection_wall_2',
      name: 'Mur de Protection',
      type: 'static',
      modelId: 'invisible_wall',
      position: [9, 0, 21],
      rotation: [0, -0.3, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        emotion: 'shame',
        color: '#8b0000',
      },
    },

    // Créations cachées d'Eärwen (visibles en spectral - magnifiques !)
    {
      id: 'obj_hidden_art_1',
      name: 'Dessin Caché',
      type: 'interactive',
      modelId: 'magical_painting',
      position: [6, 1.5, 22],
      rotation: [0, 0.4, 0],
      scale: [1.5, 1.5, 0.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        hidden: true,
        talent: 'art',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Un dessin magnifique ! Eärwen a représenté les concepts de magie en IMAGES au lieu de textes. C\'est... brillant !',
          },
        },
      ],
    },
    {
      id: 'obj_hidden_sculpture',
      name: 'Sculpture Cachée',
      type: 'interactive',
      modelId: 'sculpture',
      position: [10, 0, 22],
      rotation: [0, -0.2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        hidden: true,
        talent: 'sculpture',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une rune sculptée dans l\'argile ! Elle ne peut pas ÉCRIRE les runes, mais elle peut les SCULPTER !',
          },
        },
      ],
    },
    {
      id: 'obj_hidden_dance',
      name: 'Trace de Danse Magique',
      type: 'interactive',
      modelId: 'magic_circle',
      position: [8, 0.1, 24],
      rotation: [Math.PI / 2, 0, 0],
      scale: [2, 2, 0.1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        hidden: true,
        talent: 'kinesthetic',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Des traces de pas... Une chorégraphie magique ! Elle canalise la magie par le MOUVEMENT au lieu des mots !',
          },
        },
      ],
    },

    // === LE MAÎTRE ELFE ===
    {
      id: 'obj_elf_master',
      name: 'Maître Elrohir',
      type: 'interactive',
      modelId: 'npc_elf_master',
      position: [18, 0, 14],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        state: 'strict',
        dialogueId: 'master_intro',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Maître Elrohir: "Eärwen refuse d\'apprendre comme les autres. En 2000 ans d\'enseignement, je n\'ai jamais vu une elfe aussi... difficile."',
          },
        },
      ],
    },

    // Autres elfes "parfaits" (pour contraste)
    {
      id: 'obj_perfect_elf_1',
      name: 'Elfe Studieux',
      type: 'static',
      modelId: 'npc_elf',
      position: [16, 0, 10],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_perfect_elf_2',
      name: 'Elfe Gracieuse',
      type: 'static',
      modelId: 'npc_elf',
      position: [20, 0, 10],
      rotation: [0, 0.3, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Ami potentiel d'Eärwen
    {
      id: 'obj_elf_friend',
      name: 'Lindel',
      type: 'interactive',
      modelId: 'npc_elf_young',
      position: [12, 0, 18],
      rotation: [0, Math.PI / 2, 0],
      scale: [0.95, 0.95, 0.95],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'lindel_dialogue',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Lindel: "Eärwen ? Elle est différente, mais... j\'aime bien ses dessins. Ils m\'aident à comprendre la magie mieux que les livres."',
          },
        },
      ],
    },

    // === ÉPREUVES TRADITIONNELLES ===

    // Pupitre avec texte à réciter
    {
      id: 'obj_recitation_desk',
      name: 'Pupitre de Récitation',
      type: 'interactive',
      modelId: 'desk_elvish',
      position: [20, 0, 20],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        test: 'recitation',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Le pupitre de récitation. Traditionnellement, les elfes doivent réciter ce texte de mémoire. Mais Eärwen pourrait le DESSINER à la place...',
          },
        },
      ],
    },

    // Cercle de sort
    {
      id: 'obj_spell_circle',
      name: 'Cercle d\'Incantation',
      type: 'interactive',
      modelId: 'magic_circle',
      position: [22, 0.1, 8],
      rotation: [Math.PI / 2, 0, 0],
      scale: [3, 3, 0.1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        test: 'spell',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Le cercle d\'incantation. Les sorts doivent être prononcés... mais ils peuvent aussi être DANSÉS.',
          },
        },
      ],
    },

    // Table de calligraphie
    {
      id: 'obj_calligraphy_table',
      name: 'Table de Calligraphie',
      type: 'interactive',
      modelId: 'writing_desk',
      position: [6, 0, 8],
      rotation: [0, Math.PI / 6, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        test: 'calligraphy',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Table de calligraphie des runes. Les runes doivent être écrites... mais elles peuvent aussi être SCULPTÉES dans l\'argile.',
          },
        },
      ],
    },

    // Argile pour sculpter (outil alternatif)
    {
      id: 'obj_clay',
      name: 'Bloc d\'Argile',
      type: 'collectible',
      modelId: 'clay_block',
      position: [4, 0.5, 10],
      rotation: [0, 0.1, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'tool_clay',
            itemName: 'Argile Elfique',
            itemDescription: 'De l\'argile magique. Parfaite pour sculpter des runes au lieu de les écrire.',
            itemIcon: 'orb',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Tu récupères l\'argile. Eärwen pourrait SCULPTER les runes au lieu de les écrire !',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_clay' },
        },
      ],
    },

    // === UTILISATION DES OBJETS CROSSOVER ===

    // Zone pour utiliser le Sourire du Cheshire
    {
      id: 'obj_encouragement_spot',
      name: 'Moment de Doute',
      type: 'interactive',
      modelId: 'sparkle_area',
      position: [8, 0, 18],
      rotation: [0, 0, 0],
      scale: [2, 1, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        needsItem: 'sourire_cheshire',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Eärwen a besoin d\'encouragement... Le Sourire du Cheshire pourrait l\'aider.',
          },
        },
      ],
    },

    // Zone pour utiliser le Walkie-Talkie
    {
      id: 'obj_communication_spot',
      name: 'Zone de Contact',
      type: 'interactive',
      modelId: 'sparkle_area',
      position: [10, 0, 16],
      rotation: [0, 0, 0],
      scale: [2, 1, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        needsItem: 'walkie_talkie',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Tu pourrais utiliser le Walkie-Talkie pour que les autres personnages encouragent Eärwen !',
          },
        },
      ],
    },

    // === DÉCORS FONDCOMBE ===

    // Cascades
    {
      id: 'obj_waterfall_1',
      name: 'Cascade',
      type: 'static',
      modelId: 'waterfall',
      position: [2, 0, 14],
      rotation: [0, 0, 0],
      scale: [3, 4, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_waterfall_2',
      name: 'Petite Cascade',
      type: 'static',
      modelId: 'waterfall',
      position: [26, 0, 8],
      rotation: [0, Math.PI, 0],
      scale: [2, 3, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Rivière/Ruisseau
    {
      id: 'obj_river',
      name: 'Ruisseau Elfique',
      type: 'static',
      modelId: 'river',
      position: [14, -0.1, 10],
      rotation: [0, Math.PI / 2, 0],
      scale: [8, 0.5, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        animated: true,
        color: '#87ceeb',
      },
    },

    // Arbres elfiques (Mallorn)
    {
      id: 'obj_elvish_tree_1',
      name: 'Arbre Elfique',
      type: 'static',
      modelId: 'mallorn_tree',
      position: [4, 0, 4],
      rotation: [0, 0.2, 0],
      scale: [3, 3, 3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_elvish_tree_2',
      name: 'Arbre Elfique',
      type: 'static',
      modelId: 'mallorn_tree',
      position: [24, 0, 4],
      rotation: [0, -0.3, 0],
      scale: [2.5, 2.5, 2.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_elvish_tree_3',
      name: 'Arbre Elfique',
      type: 'static',
      modelId: 'mallorn_tree',
      position: [2, 0, 24],
      rotation: [0, 0.4, 0],
      scale: [2.8, 2.8, 2.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_elvish_tree_4',
      name: 'Arbre Elfique',
      type: 'static',
      modelId: 'mallorn_tree',
      position: [22, 0, 18],
      rotation: [0, -0.2, 0],
      scale: [2.2, 2.2, 2.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Fleurs elfiques lumineuses
    {
      id: 'obj_elvish_flowers_1',
      name: 'Fleurs Elfiques',
      type: 'static',
      modelId: 'glowing_flowers',
      position: [6, 0, 12],
      rotation: [0, 0, 0],
      scale: [2, 1, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        glows: true,
        color: '#fffacd',
      },
    },
    {
      id: 'obj_elvish_flowers_2',
      name: 'Fleurs Elfiques',
      type: 'static',
      modelId: 'glowing_flowers',
      position: [20, 0, 10],
      rotation: [0, 0.5, 0],
      scale: [1.5, 1, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        glows: true,
        color: '#e6e6fa',
      },
    },
    {
      id: 'obj_elvish_flowers_3',
      name: 'Fleurs Elfiques',
      type: 'static',
      modelId: 'glowing_flowers',
      position: [12, 0, 26],
      rotation: [0, -0.3, 0],
      scale: [1.8, 1, 1.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        glows: true,
        color: '#ffd700',
      },
    },

    // Colonnes elfiques
    {
      id: 'obj_column_1',
      name: 'Colonne Elfique',
      type: 'static',
      modelId: 'elvish_column',
      position: [10, 0, 6],
      rotation: [0, 0, 0],
      scale: [1, 2, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_column_2',
      name: 'Colonne Elfique',
      type: 'static',
      modelId: 'elvish_column',
      position: [18, 0, 6],
      rotation: [0, 0, 0],
      scale: [1, 2, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_column_3',
      name: 'Colonne Elfique',
      type: 'static',
      modelId: 'elvish_column',
      position: [6, 0, 6],
      rotation: [0, 0, 0],
      scale: [0.8, 1.8, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_column_4',
      name: 'Colonne Elfique',
      type: 'static',
      modelId: 'elvish_column',
      position: [22, 0, 6],
      rotation: [0, 0, 0],
      scale: [0.8, 1.8, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Pont elfique
    {
      id: 'obj_elvish_bridge',
      name: 'Pont Elfique',
      type: 'static',
      modelId: 'elvish_bridge',
      position: [14, 0, 12],
      rotation: [0, Math.PI / 2, 0],
      scale: [2, 1, 3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Bancs elfiques sculptés
    {
      id: 'obj_elvish_bench_1',
      name: 'Banc Elfique',
      type: 'static',
      modelId: 'elvish_bench',
      position: [8, 0, 14],
      rotation: [0, Math.PI / 4, 0],
      scale: [1.2, 1, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_elvish_bench_2',
      name: 'Banc Elfique',
      type: 'static',
      modelId: 'elvish_bench',
      position: [20, 0, 16],
      rotation: [0, -Math.PI / 3, 0],
      scale: [1.2, 1, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Lanternes elfiques
    {
      id: 'obj_lantern_1',
      name: 'Lanterne Elfique',
      type: 'static',
      modelId: 'elvish_lantern',
      position: [8, 0, 8],
      rotation: [0, 0, 0],
      scale: [1, 1.5, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        glows: true,
        color: '#ffd700',
      },
    },
    {
      id: 'obj_lantern_2',
      name: 'Lanterne Elfique',
      type: 'static',
      modelId: 'elvish_lantern',
      position: [20, 0, 8],
      rotation: [0, 0, 0],
      scale: [1, 1.5, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        glows: true,
        color: '#ffd700',
      },
    },
    {
      id: 'obj_lantern_3',
      name: 'Lanterne Elfique',
      type: 'static',
      modelId: 'elvish_lantern',
      position: [14, 0, 20],
      rotation: [0, 0, 0],
      scale: [1, 1.5, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        glows: true,
        color: '#ffd700',
      },
    },

    // Statue elfique
    {
      id: 'obj_elf_statue',
      name: 'Statue d\'Elrond',
      type: 'static',
      modelId: 'elf_statue',
      position: [14, 0, 4],
      rotation: [0, Math.PI, 0],
      scale: [1.5, 2, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Fontaine elfique centrale
    {
      id: 'obj_fountain',
      name: 'Fontaine de Fondcombe',
      type: 'static',
      modelId: 'fountain',
      position: [14, 0, 16],
      rotation: [0, 0, 0],
      scale: [2, 1.5, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        animated: true,
      },
    },

    // Parchemins et livres éparpillés
    {
      id: 'obj_scrolls_pile',
      name: 'Pile de Parchemins',
      type: 'static',
      modelId: 'scroll_pile',
      position: [16, 0, 14],
      rotation: [0, 0.2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Harpe elfique (instrument)
    {
      id: 'obj_harp',
      name: 'Harpe Elfique',
      type: 'static',
      modelId: 'harp',
      position: [10, 0, 16],
      rotation: [0, Math.PI / 6, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Pétales flottants (particules)
    {
      id: 'obj_petals_1',
      name: 'Pétales Flottants',
      type: 'static',
      modelId: 'particles_petals',
      position: [14, 2, 14],
      rotation: [0, 0, 0],
      scale: [12, 4, 12],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        animated: true,
        color: '#ffb6c1',
      },
    },

    // Éléments Mordor (spectral) - Le Mordor émotionnel d'Eärwen
    {
      id: 'obj_mordor_rock_1',
      name: 'Rocher Sombre',
      type: 'static',
      modelId: 'dark_rock',
      position: [6, 0, 16],
      rotation: [0, 0.3, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_mordor_rock_2',
      name: 'Rocher Sombre',
      type: 'static',
      modelId: 'dark_rock',
      position: [18, 0, 22],
      rotation: [0, -0.4, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_lava_pool',
      name: 'Mare de Lave',
      type: 'static',
      modelId: 'lava_pool',
      position: [22, 0, 24],
      rotation: [0, 0, 0],
      scale: [3, 0.5, 3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        glows: true,
        color: '#ff4500',
      },
    },
    {
      id: 'obj_lava_pool_2',
      name: 'Mare de Lave',
      type: 'static',
      modelId: 'lava_pool',
      position: [4, 0, 18],
      rotation: [0, 0.2, 0],
      scale: [2, 0.3, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        glows: true,
        color: '#ff4500',
      },
    },
    // Oeil de Sauron (représente le jugement qu'Eärwen ressent)
    {
      id: 'obj_eye_of_judgement',
      name: 'Oeil du Jugement',
      type: 'static',
      modelId: 'evil_eye',
      position: [14, 8, 26],
      rotation: [0, Math.PI, 0],
      scale: [3, 3, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        glows: true,
        color: '#ff0000',
        pulsing: true,
      },
    },

    // Portail retour vers le Sanctuaire
    {
      id: 'obj_portal_back',
      name: 'Portail vers le Sanctuaire',
      type: 'interactive',
      modelId: 'portal_sanctuary',
      position: [14, 0, 1],
      rotation: [0, 0, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_sanctuary',
        portalColor: '#9d8cff',
      },
      onInteract: [
        {
          type: 'loadRoom',
          params: { roomId: 'room_sanctuary' },
        },
      ],
    },

    // Portail vers la Finale (apparaît après avoir aidé Eärwen)
    {
      id: 'obj_portal_finale',
      name: 'Portail de la Révélation',
      type: 'interactive',
      modelId: 'portal_finale',
      position: [27, 0, 14],
      rotation: [0, -Math.PI / 2, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_finale',
        isActive: false,
        portalColor: '#ffd700',
        locked: true,
        unlockCondition: 'quest_middle_earth_complete',
      },
      onInteract: [
        {
          type: 'conditionalAction',
          params: {
            condition: 'quest_middle_earth_complete',
            trueAction: {
              type: 'loadRoom',
              params: { roomId: 'room_finale' },
            },
            falseAction: {
              type: 'showMessage',
              params: {
                message:
                  "L'Oracle Chevelu: *voix mystérieuse à travers le portail* \"Aide d'abord cette jeune elfe, puis viens à moi. J'ai quelque chose de TRÈS important à te révéler !\"",
              },
            },
          },
        },
      ],
    },

    // L'Oracle en sage elfique
    {
      id: 'obj_oracle_appearance',
      name: 'L\'Oracle Chevelu',
      type: 'interactive',
      modelId: 'npc_oracle_elvish',
      position: [26, 0, 20],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'oracle_middleearth_hint',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Oracle: *cheveux encore plus magnifiques que d\'habitude* "Les astres me disent que cette jeune elfe a besoin qu\'on croie en elle !" Eärwen: "Vos cheveux sont... incroyables." Oracle: *ému* "Enfin quelqu\'un qui COMPREND."',
          },
        },
      ],
    },
  ],
  puzzles: [
    // Découvrir ses forces
    {
      id: 'puzzle_discover_strengths',
      name: 'Les Forces Cachées',
      type: 'observation',
      state: 'active',
      description: 'Découvre les talents cachés d\'Eärwen en observant en mode spectral.',
      hint: 'Passe en mode spectral près d\'Eärwen pour voir ses créations cachées.',
      requiredWorld: 'spectral',
      linkedObjectIds: ['obj_hidden_art_1', 'obj_hidden_sculpture', 'obj_hidden_dance'],
      solution: {
        type: 'state',
        expected: { discovered: ['art', 'sculpture', 'kinesthetic'] },
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Tu as découvert les talents cachés d\'Eärwen ! Elle n\'apprend pas moins bien, elle apprend DIFFÉREMMENT.',
          },
        },
      ],
    },
    // Le test alternatif
    {
      id: 'puzzle_alternative_test',
      name: 'Le Test Alternatif',
      type: 'multi',
      state: 'locked',
      description: 'Aide Eärwen à réussir le test en utilisant SES méthodes.',
      linkedObjectIds: ['obj_recitation_desk', 'obj_spell_circle', 'obj_calligraphy_table'],
      solution: {
        type: 'state',
        expected: {
          recitation: 'drawing',
          spell: 'dance',
          calligraphy: 'sculpture',
        },
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Le Maître Elrohir observe, stupéfait. "En 2000 ans... personne n\'avait fait ça. Tu m\'as appris quelque chose, jeune elfe."',
          },
        },
        {
          type: 'addToInventory',
          params: {
            itemId: 'feuille_nouveau_depart',
            itemName: 'Feuille de Nouveau Départ',
            itemDescription: 'Une feuille d\'or de Fondcombe. Symbole de transformation et de croissance.',
            itemIcon: 'gem',
          },
        },
      ],
    },
    // Crossover complet
    {
      id: 'puzzle_crossover_support',
      name: 'Le Soutien des Mondes',
      type: 'collect',
      state: 'locked',
      description: 'Utilise tous les objets des aventures précédentes pour soutenir Eärwen.',
      linkedObjectIds: ['obj_encouragement_spot', 'obj_communication_spot'],
      solution: {
        type: 'items',
        expected: ['sourire_cheshire', 'walkie_talkie', 'griffe_resilience', 'epee_perseverance'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Les voix de tous ceux que tu as aidés résonnent : "Tu peux le faire, Eärwen !" Le Chapelier, Vékeuna, Blue, Tyrion... tous sont avec elle.',
          },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_sanctuary',
      position: [14, 0, 1],
      direction: 'north',
      locked: false,
    },
    {
      targetRoomId: 'room_finale',
      position: [27, 0, 14],
      direction: 'east',
      locked: true,
      unlockCondition: 'quest_middle_earth_complete',
    },
  ],
  npcs: [
    // Poney de hobbit
    {
      id: 'npc_hobbit_pony',
      type: 'deer', // Utilise deer comme base
      name: 'Poney de la Comté',
      position: [4, 0, 18],
      scale: 1.2,
      idleAnimation: 'graze',
      petAnimation: 'nuzzle',
      petRadius: 2.5,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Aigle géant
    {
      id: 'npc_great_eagle',
      type: 'eagle',
      name: 'Grand Aigle',
      position: [24, 0, 12],
      scale: 2,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 3,
      petCooldown: 5000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Lapin du jardin
    {
      id: 'npc_garden_rabbit',
      type: 'rabbit',
      name: 'Lapin de Fondcombe',
      position: [12, 0, 8],
      scale: 0.8,
      idleAnimation: 'sit',
      petAnimation: 'jump',
      petRadius: 1.5,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Papillon enchanté
    {
      id: 'npc_enchanted_butterfly',
      type: 'butterfly',
      name: 'Papillon Enchanté',
      position: [16, 1, 22],
      scale: 1.5,
      idleAnimation: 'fly',
      petAnimation: 'spin',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
  ],
};
